import { useMemo, type ReactElement } from 'react';

import { LevelingComparison } from '../components/resource';
import { buildLevelingComparison } from '../integration/adapters';
import { useProjectStore } from '../store/project-store';

/**
 * 資源撫平的進行中狀態與前後對照。
 *
 * 撫平在中型專案上約需 1～2 秒的**同步**運算，期間 JS 主執行緒完全被佔住 ——
 * 所以進行中的遮罩必須在運算開始「之前」就已經畫出來（store 的
 * `levelResources()` 先同步設 `isRunning` 再讓出一個 macrotask，見該處註解）。
 * 這個元件只負責呈現，不決定時機。
 */
export function LevelingPanel(): ReactElement | null {
  const isRunning = useProjectStore((state) => state.leveling.isRunning);
  const result = useProjectStore((state) => state.leveling.result);
  const projectBefore = useProjectStore((state) => state.leveling.projectBefore);
  const error = useProjectStore((state) => state.leveling.error);
  const dismiss = useProjectStore((state) => state.dismissLevelingResult);

  const comparison = useMemo(
    () => (result !== null && projectBefore !== null ? buildLevelingComparison(projectBefore, result) : null),
    [result, projectBefore],
  );

  if (isRunning) {
    return (
      <div
        data-testid="leveling-progress"
        className="absolute inset-0 z-20 flex items-center justify-center bg-white/80 backdrop-blur-sm dark:bg-slate-900/80"
      >
        <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white px-8 py-6 shadow-lg dark:border-slate-700 dark:bg-slate-800">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
          <p className="text-sm font-medium">資源撫平計算中…</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">大型專案約需數秒，請稍候。</p>
        </div>
      </div>
    );
  }

  if (error !== null) {
    return (
      <div
        data-testid="leveling-error"
        className="absolute inset-0 z-20 flex items-center justify-center bg-white/80 dark:bg-slate-900/80"
      >
        <div className="max-w-md rounded-lg border border-red-300 bg-white px-6 py-5 text-center shadow-lg dark:border-red-700 dark:bg-slate-800">
          <p className="text-sm font-medium text-red-600 dark:text-red-400">資源撫平失敗</p>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{error}</p>
          <button
            type="button"
            onClick={dismiss}
            className="mt-4 rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-700"
          >
            關閉
          </button>
        </div>
      </div>
    );
  }

  if (comparison === null) return null;

  return (
    <div
      data-testid="leveling-comparison-panel"
      className="absolute inset-0 z-20 flex flex-col bg-white/95 backdrop-blur-sm dark:bg-slate-900/95"
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2 dark:border-slate-700">
        <h2 className="text-sm font-semibold">資源撫平：前後對照</h2>
        <button
          type="button"
          onClick={dismiss}
          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-700"
        >
          關閉
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <LevelingComparison
          rows={comparison.rows}
          originalFinish={comparison.originalFinish}
          leveledFinish={comparison.leveledFinish}
          finishDelayMinutes={comparison.finishDelayMinutes}
          remainingOverallocationCount={comparison.remainingOverallocationCount}
        />
      </div>
    </div>
  );
}
